// mirror of auware/.../frontend/src/components/ui/Badge.tsx (see frontend/CLAUDE.md mirror policy).
import { forwardRef, type HTMLAttributes } from "react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Visual style of the badge. */
type BadgeVariant = "default" | "info" | "success" | "warning" | "error";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** Visual variant. Defaults to `"default"`. */
  variant?: BadgeVariant;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

// ``whitespace-nowrap`` keeps short labels like "AI agent" on a
// single line when the badge's table cell is narrow; without it the
// Badge soft-wraps inside its pill shape. ``shrink-0`` is applied at
// call sites where a flex parent might otherwise squeeze the pill.
const baseClasses =
  "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium";

const variantClasses: Record<BadgeVariant, string> = {
  default: "bg-neutral-100 text-neutral-700",
  info: "bg-blue-100 text-blue-700",
  success: "bg-emerald-100 text-emerald-700",
  warning: "bg-amber-100 text-amber-700",
  error: "bg-red-100 text-red-700",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Small status badge for labels and indicators.
 *
 * ```tsx
 * <Badge variant="success">Active</Badge>
 * <Badge variant="error">Failed</Badge>
 * ```
 */
const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className = "", variant = "default", children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={`${baseClasses} ${variantClasses[variant]} ${className}`}
        {...props}
      >
        {children}
      </span>
    );
  },
);

Badge.displayName = "Badge";

export default Badge;
